import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, message } from 'antd';
import type { Manager, Club } from '@/types/models';
import { apiService } from '@/services/api.service';
import { API_ENDPOINTS } from '@/constants/api.constants';
import { logger } from '@/utils/logger';

interface EditManagerModalProps {
  visible: boolean;
  manager: Manager | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const EditManagerModal: React.FC<EditManagerModalProps> = ({
  visible,
  manager,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(false);

  // Загрузка списка клубов
  const fetchClubs = async () => {
    try {
      setLoading(true);
      const { data } = await apiService.get<{ data: Club[], status: number }>(API_ENDPOINTS.CLUBS.BASE);
      if (data?.data) {
        setClubs(data.data);
      }
    } catch (error) {
      logger.error('Error fetching clubs:', error);
      message.error('Ошибка при загрузке списка клубов');
    } finally {
      setLoading(false);
    }
  };

  // Загружаем список клубов при открытии модального окна
  useEffect(() => {
    if (visible) {
      fetchClubs();
    }
  }, [visible]);

  // Функция для загрузки детальной информации о менеджере
  const fetchManagerDetails = async (managerId: number) => {
    try {
      setLoading(true);
      const { data } = await apiService.get<{ data: Manager, status: number }>(
        `${API_ENDPOINTS.MANAGERS.BASE}/${managerId}`
      );

      if (data?.data) {
        const managerData = data.data;
        // Теперь используем прямое поле clubId
        const clubId = managerData.clubId;

        form.setFieldsValue({
          firstName: managerData.firstName,
          lastName: managerData.lastName,
          middleName: managerData.middleName,
          email: managerData.email,
          role: managerData.role,
          status: managerData.status,
          clubId: clubId
        });
      }
    } catch (error) {
      logger.error('Error fetching manager details:', error);
      message.error('Ошибка при загрузке данных менеджера');
    } finally {
      setLoading(false);
    }
  };

  // Загружаем данные менеджера при открытии модального окна
  useEffect(() => {
    if (visible && manager) {
      // Сначала устанавливаем базовые поля из переданного объекта
      form.setFieldsValue({
        firstName: manager.firstName,
        lastName: manager.lastName,
        email: manager.email,
        role: manager.role || 'manager',
        status: manager.status || 'active',
        // Теперь используем прямое поле clubId
        clubId: manager.clubId || manager.clubManagers?.[0]?.clubId
      });

      // Затем загружаем актуальные данные менеджера с сервера
      fetchManagerDetails(manager.id);
    }
  }, [visible, manager, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!manager) return;

      // Подготовка данных для отправки
      const updateData = {
        firstName: values.firstName,
        lastName: values.lastName,
        middleName: values.middleName,
        email: values.email,
        role: values.role,
        status: values.status,
        clubId: values.clubId // Теперь обновляем clubId напрямую
      };

      // Обновляем основные данные менеджера и clubId в одном запросе
      await apiService.put(`${API_ENDPOINTS.MANAGERS.BASE}/${manager.id}`, updateData);

      message.success('Менеджер успешно обновлен');
      form.resetFields();
      onSuccess();
    } catch (error: any) {
      logger.error('Error updating manager:', error);

      // Проверяем наличие сообщения об ошибке в ответе API
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else if (error.message && error.message.includes('already associated with this club')) {
        message.error('Менеджер уже связан с этим клубом');
      } else {
        message.error('Ошибка при обновлении менеджера');
      }
    }
  };

  return (
    <Modal
      title="Редактировать менеджера"
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ role: 'manager', status: 'active' }}
      >
        <Form.Item
          name="lastName"
          label="Фамилия"
          rules={[{ required: true, message: 'Введите фамилию' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="firstName"
          label="Имя"
          rules={[{ required: true, message: 'Введите имя' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="middleName"
          label="Отчество"
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Введите email' },
            { type: 'email', message: 'Введите корректный email' }
          ]}
        >
          <Input />
        </Form.Item>

        {/* Поле organization удалено, теперь организация определяется через клуб */}

        <Form.Item
          name="role"
          label="Роль"
          rules={[{ required: true, message: 'Выберите роль' }]}
        >
          <Select>
            <Select.Option value="manager">Менеджер</Select.Option>
            <Select.Option value="head_manager">Главный менеджер</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="status"
          label="Статус"
          rules={[{ required: true, message: 'Выберите статус' }]}
        >
          <Select>
            <Select.Option value="active">Активен</Select.Option>
            <Select.Option value="inactive">Неактивен</Select.Option>
            <Select.Option value="pending">Ожидает подтверждения</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="clubId"
          label="Клуб"
        >
          <Select
            placeholder="Выберите клуб"
            loading={loading}
            allowClear
          >
            {clubs.map(club => (
              <Select.Option key={club.id} value={club.id}>{club.name}</Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditManagerModal;